import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { Ticket, X, Clock, CheckCircle2, XCircle, ChevronRight, Copy, Mail, Share2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { getMyVouchers, type Voucher } from "@/lib/rewards/voucherEngine";
import { sendVoucherEmail } from "@/lib/rewards/voucherDelivery";
import { shareWhatsApp, downloadVoucherPdf } from "@/lib/rewards/voucherShare";
import { trackEvent } from "@/lib/audit";
import { useLanguage } from "@/hooks/useLanguage";
import { useModalRegistration } from "@/lib/mobileFloatingControls";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MyRewardsCenter({ open, onClose }: Props) {
  const { t } = useLanguage();
  useModalRegistration(open, "my_rewards_center");
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"active" | "history">("active");
  const [selected, setSelected] = useState<Voucher | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      // FASE 5 — Reset state on close
      setSelected(null);
      setTab("active");
      void trackEvent("rewards_modal_state_reset", { modal: "my_rewards_center" });
      return;
    }
    void trackEvent("reward_center_opened", {});
    setLoading(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    void getMyVouchers().then((v) => {
      setVouchers(v);
      setLoading(false);
    });
  }, [open]);

  const { active, history } = useMemo(() => {
    return {
      active: vouchers.filter((v) => v.status === "active"),
      history: vouchers.filter((v) => v.status !== "active"),
    };
  }, [vouchers]);

  if (!open) return null;

  const list = tab === "active" ? active : history;

  const onBackdrop = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center backdrop-blur-md"
      style={{ background: "rgba(8,8,12,0.85)" }}
      onClick={onBackdrop}
      onPointerDownCapture={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-border/60 shadow-2xl"
        style={{ background: "rgba(8,8,12,0.98)", backdropFilter: "blur(8px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <Ticket className="h-4 w-4 text-[oklch(0.85_0.18_85)]" />
            <div className="text-sm font-bold">{t("my_rewards_title")}</div>
          </div>
          <button onClick={onClose} aria-label={t("close")} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex border-b border-border/60 text-[11px] font-bold">
          <button
            onClick={() => setTab("active")}
            className={`flex-1 py-2 transition ${tab === "active" ? "text-foreground border-b-2 border-primary" : "text-muted-foreground"}`}
          >
            {t("my_rewards_active")} ({active.length})
          </button>
          <button
            onClick={() => setTab("history")}
            className={`flex-1 py-2 transition ${tab === "history" ? "text-foreground border-b-2 border-primary" : "text-muted-foreground"}`}
          >
            {t("my_rewards_history")} ({history.length})
          </button>
        </div>

        <div ref={scrollRef} className="max-h-[70vh] overflow-y-auto p-3 space-y-2 bg-[rgb(8,8,12)]">
          {loading && <div className="py-8 text-center text-xs text-muted-foreground">{t("loading")}</div>}
          {!loading && list.length === 0 && (
            <div className="py-10 text-center text-xs text-foreground/70">
              {tab === "active" ? t("my_rewards_empty_active") : t("my_rewards_empty_history")}
            </div>
          )}
          {list.map((v) => (
            <button
              key={v.id}
              onClick={(e) => { e.stopPropagation(); setSelected(v); }}
              className="flex w-full items-center gap-3 rounded-xl border border-border/70 bg-[rgb(18,18,24)] p-3 text-left transition hover:border-primary/60 hover:bg-[rgb(24,24,32)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent/60 text-2xl">🎁</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold truncate text-foreground">
                    {(v.metadata as { reward_name?: string } | null)?.reward_name ?? v.code}
                  </div>
                  {v.partner && (
                    <span className="rounded bg-primary/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
                      {v.partner.name}
                    </span>
                  )}
                  <StatusBadge status={v.status} t={t} />
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-foreground/70">
                  <span className="font-mono text-foreground/90">{v.code}</span>
                  {v.status === "active" && v.expires_at && (
                    <span className="flex items-center gap-0.5">
                      <Clock className="h-3 w-3" /> {new Date(v.expires_at).toLocaleDateString()}
                    </span>
                  )}
                  {v.status === "redeemed" && (
                    <span className="flex items-center gap-0.5 text-[oklch(0.78_0.18_145)]">
                      <CheckCircle2 className="h-3 w-3" /> {t("voucher_status_redeemed")}
                    </span>
                  )}
                  {v.status === "expired" && (
                    <span className="flex items-center gap-0.5 text-foreground/60">
                      <XCircle className="h-3 w-3" /> {t("voucher_status_expired")}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>

        <div className="border-t border-border/60 bg-[rgb(8,8,12)] px-4 py-2 text-center text-[10px] text-foreground/70">
          {t("my_rewards_footer")}
        </div>
      </div>

      {selected && <VoucherDetail voucher={selected} onClose={() => setSelected(null)} />}
    </div>
  );
  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}

function StatusBadge({ status, t }: { status: string; t: (k: string) => string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: t("my_rewards_active"), cls: "bg-emerald-500/20 text-emerald-400" },
    redeemed: { label: t("voucher_status_redeemed"), cls: "bg-primary/20 text-primary" },
    expired: { label: t("voucher_status_expired"), cls: "bg-muted text-muted-foreground" },
    cancelled: { label: "Cancelled", cls: "bg-red-500/20 text-red-400" },
  };
  const m = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground" };
  return <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${m.cls}`}>{m.label}</span>;
}

function VoucherDetail({ voucher, onClose }: { voucher: Voucher; onClose: () => void }) {
  const { t, lang } = useLanguage();
  const name = (voucher.metadata as { reward_name?: string } | null)?.reward_name ?? voucher.code;
  const [busy, setBusy] = useState<string | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(voucher.code);
      toast.success(t("voucher_code_copied"));
    } catch {
      toast.error(t("voucher_code_copy_failed"));
    }
  };
  const onResend = async () => {
    setBusy("email");
    const r = await sendVoucherEmail(voucher, { isResend: true });
    setBusy(null);
    if (r.ok) toast.success(t("voucher_email_resent"));
    else if (r.reason === "no_recipient") toast.error(t("voucher_email_no_recipient"));
    else if (r.reason === "delivery_disabled") toast.message(t("voucher_email_pending_config"));
    else toast.error(t("voucher_email_failed"));
  };
  const onWhatsApp = async () => { setBusy("wa"); await shareWhatsApp(voucher, lang); setBusy(null); };
  const onPdf = async () => { setBusy("pdf"); try { await downloadVoucherPdf(voucher, lang); } catch { toast.error(t("voucher_pdf_failed")); } setBusy(null); };
  const node = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 backdrop-blur-md"
      style={{ background: "rgba(8,8,12,0.92)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-border/60 p-5 shadow-2xl"
        style={{ background: "rgba(12,12,18,0.99)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-base font-black text-foreground">{name}</div>
            {voucher.partner && (
              <div className="text-[10px] text-foreground/70">{voucher.partner.name}</div>
            )}
          </div>
          <button onClick={onClose} aria-label={t("close")}>
            <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </button>
        </div>
        <div className="flex justify-center rounded-xl bg-white p-4">
          <QRCodeSVG value={voucher.qr_payload} size={196} level="M" />
        </div>
        <div className="mt-2 text-center text-[10px] font-bold uppercase tracking-wider text-foreground/60">{t("voucher_action_pdf").includes("QR") ? "" : "QR"}</div>
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-[rgb(20,20,28)] px-3 py-2 border border-border/60">
          <code className="text-sm font-bold tracking-widest text-foreground">{voucher.code}</code>
          <button onClick={copy} className="flex items-center gap-1 rounded-md bg-primary/20 px-2 py-1 text-[11px] font-bold text-primary hover:bg-primary/30">
            <Copy className="h-3 w-3" /> {t("copy")}
          </button>
        </div>
        {voucher.expires_at && (
          <div className="mt-3 text-center text-[11px] font-medium text-foreground/80">
            {t("voucher_expires_on")} {new Date(voucher.expires_at).toLocaleString()}
          </div>
        )}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={onPdf} disabled={!!busy} className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-[rgb(20,20,28)] p-2 text-[10px] font-bold text-foreground hover:bg-[rgb(28,28,38)] disabled:opacity-50">
            <FileDown className="h-4 w-4" /> {t("voucher_action_pdf")}
          </button>
          <button onClick={onResend} disabled={!!busy} className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-[rgb(20,20,28)] p-2 text-[10px] font-bold text-foreground hover:bg-[rgb(28,28,38)] disabled:opacity-50">
            <Mail className="h-4 w-4" /> {t("voucher_action_email")}
          </button>
          <button onClick={onWhatsApp} disabled={!!busy} className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-[rgb(20,20,28)] p-2 text-[10px] font-bold text-foreground hover:bg-[rgb(28,28,38)] disabled:opacity-50">
            <Share2 className="h-4 w-4" /> {t("voucher_action_whatsapp")}
          </button>
        </div>
        <div className="mt-3 rounded-lg border border-border/60 bg-[rgb(14,14,20)] p-3 text-[11px] leading-relaxed text-foreground/85">
          {t("voucher_instructions")}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}